/**
 * firebase/firebase.js — Lazy, idempotent Firebase JS SDK initialization.
 *
 * The SDK is only initialized if all REACT_APP_FIREBASE_* env vars are set.
 * When they're missing (current state, before the user creates the Firebase
 * project), this module exports `firebaseReady = false` and `getAuthSafe()`
 * returns null — the UI hides the Firebase login button gracefully.
 *
 * Once the user fills the env vars in frontend/.env and restarts the build,
 * the SDK lights up automatically — no other code changes needed.
 */
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';

const cfg = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

export const firebaseReady = Boolean(
  cfg.apiKey && cfg.authDomain && cfg.projectId && cfg.appId
);

let _app = null;
function ensureApp() {
  if (!firebaseReady) return null;
  if (_app) return _app;
  _app = getApps()[0] || initializeApp(cfg);
  return _app;
}

export function getAuthSafe() {
  const app = ensureApp();
  return app ? getAuth(app) : null;
}

/**
 * Pop the Google sign-in flow + exchange the resulting ID token with our
 * backend for a DIS2ORT session token.
 */
export async function signInWithGoogleViaFirebase() {
  const auth = getAuthSafe();
  if (!auth) throw new Error('Firebase Auth not configured — set REACT_APP_FIREBASE_* env vars.');
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const result = await signInWithPopup(auth, provider);
  const idToken = await result.user.getIdToken();
  return { idToken, firebaseUser: result.user };
}

export async function signOutFirebase() {
  const auth = getAuthSafe();
  if (auth) await signOut(auth);
}
